import React from "react";
import { AgentCard } from "./AgentCard";
import { Button } from "@/components";
import { Plus, Bot } from "lucide-react";
import type { Agent } from "../interfaces";

interface AgentListProps {
  agents: Agent[];
  onEdit: (agent: Agent) => void;
  onDelete: (agentId: number) => void;
  onCreateNew: () => void;
  onView?: (agent: Agent) => void;
  isLoading?: boolean;
}

export const AgentList: React.FC<AgentListProps> = ({
  agents,
  onEdit,
  onDelete,
  onCreateNew,
  onView,
  isLoading = false,
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-sm text-muted-foreground">Loading agents...</p>
        </div>
      </div>
    );
  }

  if (agents.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="max-w-md mx-auto">
          <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            <Bot className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold mb-2">No agents yet</h3>
          <p className="text-sm text-muted-foreground mb-6">
            Get started by creating AI agents for your content plan
          </p>
          <Button onClick={onCreateNew}>
            <Plus className="h-4 w-4 mr-2" />
            Create Agents
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold">Agents</h2>
          <p className="text-sm text-muted-foreground">
            Manage your AI agents and their content generation settings
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={onCreateNew}>
            <Plus className="h-4 w-4 mr-2" />
            Create Agents
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {agents.map((agent) => (
          <AgentCard
            key={agent.id}
            agent={agent}
            onEdit={onEdit}
            onDelete={onDelete}
            onView={onView}
          />
        ))}
      </div>
    </div>
  );
};
